import React, { useState, FC, useEffect } from 'react'
import { FieldArray } from 'formik'
import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'

import GoogleClient from '@mortgage-pos/ui/services/GoogleClient'
import { RepeatableManualFields } from './RepeatableManualFields'
import { AutoCompleteField } from './AutoCompleteField'
import { useAddressFieldsAutoComplete } from './useAddressFieldsAutoComplete'
import { isCompleteAddress } from './helpers'
import { AutoCompleteErrorBoundary } from './AutoCompleteErrorBoundary'
import { RepeatableAddressFields } from './addressAutoComplete'

export type Props = {
  name: string
  title?: string
  questionGroup: string
  modifierClass: string
  subTitle?: string
}

export const RepeatableAutoComplete: FC<Props> = ({
  name,
  title,
  questionGroup,
  modifierClass,
  subTitle,
}) => {
  const [addressFields, setAddressFields] = useState([])
  const { fieldInputted, elementClicked } = useStoreActions(
    (state) => state.tracking
  )
  const { userLocation } = useStoreState((state) => state.address)

  const { inputText, setInputText, results } =
    useAddressFieldsAutoComplete(userLocation)

  const [enterAddressManually, setEnterAddressManually] = useState(false)
  const [fullAddressNotFound, setFullAddressNotFound] = useState(false)

  useEffect(() => {
    const repeatableAddressFields = generateNamedAddressFields({
      questionGroup,
    })

    setAddressFields(repeatableAddressFields)
  }, [questionGroup])

  const handleInputChange = async (e, setFieldValue) => {
    const { value } = e.target

    setInputText(value)
    if (isCompleteAddress(value, results)) {
      try {
        const address = await GoogleClient.getAddressDetails(value)
        addressFields.forEach(
          ({ defaultField, uniqueField }: RepeatableAddressFields) => {
            setFieldValue(uniqueField, address[defaultField] || '')
          }
        )

        // If any address value is missing from geocoding, show the full address fields
        defaultAddressFields.forEach((field) => {
          if (field !== 'apt' && !address[field]) {
            setEnterAddressManually(true)
            setFullAddressNotFound(true)
            fieldInputted({
              questionName: 'newAutoCompleteAddressComponent',
              value: 'Full address not found',
              subTitle: subTitle,
            })
            return
          }
        })
      } catch (e) {
        setEnterAddressManually(true)
        setFullAddressNotFound(true)
      }
    }
  }

  return (
    <AutoCompleteErrorBoundary>
      <FieldArray name={questionGroup} key={`question-group-${questionGroup}`}>
        {() => (
          <div
            className={`address-fields-auto-complete ${modifierClass || ''}`}
          >
            {title ? (
              <h2 className="address-fields-auto-complete__title">{title}</h2>
            ) : null}
            {fullAddressNotFound ? (
              <h3 className="address-fields-auto-complete__address-not-found">
                We had trouble validating your address, please enter here.
              </h3>
            ) : null}
            {enterAddressManually ? (
              <RepeatableManualFields addressFields={addressFields} />
            ) : (
              <AutoCompleteField
                addressFields={addressFields}
                name={name}
                handleInputChange={handleInputChange}
                results={results}
                inputText={inputText}
                setInputText={setInputText}
                setEnterAddressManually={setEnterAddressManually}
                elementClicked={elementClicked}
              />
            )}
          </div>
        )}
      </FieldArray>
    </AutoCompleteErrorBoundary>
  )
}

const defaultAddressFields = ['street', 'apt', 'city', 'state', 'zipCode']

const generateNamedAddressFields = ({
  questionGroup,
}): RepeatableAddressFields[] =>
  defaultAddressFields.map((field) => ({
    uniqueField: `${questionGroup}.${field}`,
    defaultField: field,
  }))
